import React, { ReactElement, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import { getOverlayBounds, OverlayPlacement, placeOverlay } from '@utils/contentBounds.ts';

export interface ActionMenuItem {
  key: string;
  label: string;
  icon?: ReactElement;
  onClick: () => void;
  disabled?: boolean;
  /** Destructive actions are shown in the error colour. */
  danger?: boolean;
  /** Shown in the brand colour, e.g. actions offered as a continuation of a brand-coloured button. */
  brand?: boolean;
  /** Shown as a tooltip on a disabled item, saying why it can't be used. */
  disabledReason?: string;
  /** Compact menus draw a divider only before items that ask for one (the default menu divides every item). */
  separatorBefore?: boolean;
  /** Starts a titled group of items (e.g. "Current view" above its formats); compact menus divide it from the group before. */
  section?: { title: string; hint?: string };
  dataTestId?: string;
}

interface ComponentProps {
  items: ActionMenuItem[];
  /** Caption above the actions, saying what the menu is for. */
  heading?: string;
  /** Glyph for the trigger button. */
  icon: ReactElement;
  /** Trigger label; omitted for an icon-only trigger. */
  label?: string;
  mode?: 'table' | 'outlined-rectangle' | 'text-only' | 'link';
  size?: 'small' | 'big';
  tooltip?: string;
  dataTestId?: string;
  /** Extra classes for the trigger button, e.g. to shrink it into a dense row. */
  triggerClassName?: string;
  /** Disables the trigger, e.g. while the action it offers is already running. */
  disabled?: boolean;
  /** Grey note under the actions, divided from them — says something about the menu's subject rather than offering an action. */
  footer?: string;
  /** `compact`: smaller rows and dividers only where an item asks for one (Figma tab menu). Defaults to today's look. */
  density?: 'default' | 'compact';
  /** Turns the trigger glyph upside down while the menu is open — for a caret that points at the menu. */
  rotateIconWhenOpen?: boolean;
}

/** Keeps the menu clear of the window edges. */
const VIEWPORT_MARGIN = 8;

type MenuPosition = OverlayPlacement & { left: number };

const samePosition = (a: MenuPosition | null, b: MenuPosition) => !!a && a.top === b.top && a.bottom === b.bottom && a.left === b.left && a.maxHeight === b.maxHeight;

/**
 * A button that opens a list of actions — one control in place of a row of icon buttons.
 *
 * The menu is rendered into the body so no scrolling ancestor can clip it, which means it has to be
 * placed by hand: `placeOverlay` picks the side and the height, and the band keeps it inside the
 * content column rather than the window.
 */
const ActionMenu: React.FC<ComponentProps> = ({
  items,
  heading,
  icon,
  label,
  mode = 'table',
  size = 'small',
  tooltip,
  dataTestId,
  triggerClassName,
  density = 'default',
  disabled = false,
  footer,
  rotateIconWhenOpen = false,
}) => {
  const compact = density === 'compact';
  const menuId = `action-menu-${useId().replace(/:/g, '-')}`;
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const openUpwardsRef = useRef<boolean | null>(null);

  useLayoutEffect(() => {
    if (!visible) {
      // Dropped so a reopen measures again instead of flashing at the previous anchor's position.
      openUpwardsRef.current = null;
      setPosition(null);
      return;
    }

    const place = () => {
      const menu = menuRef.current;
      const trigger = triggerRef.current?.getBoundingClientRect();
      if (!trigger || !menu) return;

      // Clamped to the trigger's band — the content column, or the panel it lives in — rather than
      // the window, so the menu never spills over the side-panel region.
      const bounds = getOverlayBounds(triggerRef.current);
      const left = Math.max(bounds.left + VIEWPORT_MARGIN, Math.min(trigger.left, bounds.right - menu.offsetWidth - VIEWPORT_MARGIN));

      // The side is kept from the first call so that scrolling cannot move the menu under the pointer.
      // Flush against the trigger, where this menu has always sat; the margin only keeps it off the window edge.
      const placement = placeOverlay(trigger, menu.scrollHeight, { gap: 0, margin: VIEWPORT_MARGIN, openUpwards: openUpwardsRef.current ?? undefined });
      if (!placement) {
        setVisible(false);
        return;
      }
      openUpwardsRef.current = placement.bottom !== undefined;
      const next: MenuPosition = { ...placement, left };
      setPosition(current => (samePosition(current, next) ? current : next));
    };

    place();
    window.addEventListener('resize', place);
    // Capture phase: the menu has to follow whichever container actually scrolls.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [visible, items.length]);

  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setVisible(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setVisible(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible]);

  if (items.length === 0) return null;

  return (
    <div ref={triggerRef} className={'inline-flex'}>
      <RingilButton
        mode={mode}
        size={size}
        icon={
          rotateIconWhenOpen ? React.cloneElement(icon, { className: clsx(icon.props.className, 'transition-transform duration-200', visible && 'rotate-180') }) : icon
        }
        label={label}
        // Only while closed: an open menu already says what the trigger does, and the tooltip would cover it.
        tooltip={visible ? undefined : tooltip}
        disabled={disabled}
        onClick={() => setVisible(!visible)}
        dataTestId={dataTestId}
        className={triggerClassName}
      />
      {visible &&
        createPortal(
          <div
            ref={menuRef}
            // The gutter is reserved so a classic scrollbar cannot widen the menu after `left` has been
            // measured from it, which would push a right-anchored menu past its band.
            className={clsx(
              'fixed z-[var(--z-popup)] flex flex-col overflow-y-auto [scrollbar-gutter:stable] rounded-lg bg-base-secondary shadow-[0_8px_12px_0_rgba(0,0,0,0.1)]',
              compact ? 'min-w-[250px] p-1' : 'min-w-[283px] p-2',
            )}
            // Hidden until measured: the height that decides the side is readable only once mounted.
            style={{
              top: position?.top,
              bottom: position?.bottom,
              left: position?.left,
              maxHeight: position?.maxHeight,
              visibility: position ? undefined : 'hidden',
            }}
            data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
          >
            {heading && <div className={'flex items-center px-3 py-2 text-xs font-bold tracking-[0.24px] text-content-primary'}>{heading}</div>}
            {items.map((item, index) => (
              <React.Fragment key={item.key}>
                {(compact ? item.separatorBefore || (item.section && index > 0) : index > 0 || heading) && (
                  <div className={clsx('h-px w-full shrink-0 bg-content-quaternary', compact && item.section && 'my-1')} />
                )}
                {item.section && (
                  <div className={clsx('flex flex-col gap-0.5', compact ? 'px-3 pb-1 pt-2' : 'px-4 pb-1 pt-3')}>
                    <span className={'text-xs font-bold tracking-[0.24px] text-content-primary'}>{item.section.title}</span>
                    {item.section.hint && <span className={'text-xs text-content-secondary'}>{item.section.hint}</span>}
                  </div>
                )}
                {/* The tooltip hangs on a wrapper: a disabled button fires no mouse events. */}
                <div id={`${menuId}-${index}`} className={'flex'}>
                  <button
                    type={'button'}
                    disabled={item.disabled}
                    className={clsx(
                      'flex w-full cursor-pointer items-center border-none bg-transparent text-left',
                      compact ? 'gap-3 rounded px-3 py-2 text-sm' : 'gap-4 p-4 text-[15px]',
                      'enabled:hover:bg-base-tertiary disabled:cursor-not-allowed disabled:text-content-tertiary',
                      item.danger ? 'text-complementary-error' : item.brand ? 'text-content-brand' : 'text-content-primary',
                    )}
                    onClick={() => {
                      setVisible(false);
                      item.onClick();
                    }}
                    data-testid={item.dataTestId}
                  >
                    {item.icon && <span className={'inline-flex size-4 shrink-0 items-center justify-center'}>{item.icon}</span>}
                    <span className={'flex-1 break-words'}>{item.label}</span>
                  </button>
                </div>
                {item.disabled && item.disabledReason && <CustomTooltip targetSelector={`#${menuId}-${index}`} content={item.disabledReason} />}
              </React.Fragment>
            ))}
            {footer && (
              <>
                <div className={'h-px w-full shrink-0 bg-content-quaternary'} />
                <div
                  className={clsx('text-xs text-content-secondary', compact ? 'px-3 py-2' : 'px-4 py-3')}
                  data-testid={dataTestId ? `${dataTestId}-footer` : undefined}
                >
                  {footer}
                </div>
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ActionMenu;
